import type { ReactElement, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Numeric } from '@/components/ui/numeric';
import { cn } from '@/lib/utils';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import { MatchStrip, type MatchStripProps } from './MatchStrip';

export interface MatchScoreHeaderSide {
  name: ReactNode;
  /** Team members, printed under the team name. */
  roster?: string[];
  avatar?: ReactNode;
  won: boolean;
  lost: boolean;
}

export interface MatchScoreHeaderProps {
  strip: MatchStripProps;
  sides: [MatchScoreHeaderSide, MatchScoreHeaderSide];
  gamesWon: [number, number];
  /** The game being played. Absent between games and on finished matches (the caller decides). */
  currentGame?: { number: number; score: [number, number] };
  footer?: ReactNode;
  action?: ReactNode;
}

const sideName = (s: MatchScoreHeaderSide) =>
  s.lost ? 'text-muted font-medium' : s.won ? 'font-bold' : 'font-semibold';

/**
 * The scorecard page's header card: the shared strip, a side each with the games won between them,
 * the game in progress under the score, and a footer line with an action. Presentational: the
 * caller works out who won and whether a game is current.
 */
export function MatchScoreHeader({
  strip,
  sides,
  gamesWon,
  currentGame,
  footer,
  action,
}: MatchScoreHeaderProps): ReactElement {
  const t = useTranslations('Scorecard');

  const side = (i: 0 | 1) => {
    const s = sides[i];
    return (
      <div
        data-side={i}
        className={cn(
          'flex min-w-0 flex-col gap-1.5 @md:flex-row @md:items-center @md:gap-2.5',
          i === 0 ? 'items-start' : 'items-end text-right @md:flex-row-reverse',
        )}
      >
        {s.avatar && <div className="shrink-0">{s.avatar}</div>}
        <div className="min-w-0">
          <div
            data-side-name={i}
            className={cn('text-[15px] @md:text-[17px]', NAME_WRAP, sideName(s))}
          >
            {s.name}
          </div>
          {s.roster && s.roster.length > 0 && (
            <ul data-roster className="text-muted mt-0.5 text-xs">
              {s.roster.map((member, k) => (
                <li key={k} className={NAME_WRAP}>
                  {member}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const total = (i: 0 | 1) => (
    <Numeric data-score-side={i} className={cn('font-heading', sides[i].lost && 'text-muted')}>
      {gamesWon[i]}
    </Numeric>
  );

  return (
    <div
      data-match-header
      className="bg-card border-border-strong @container overflow-hidden rounded-lg border shadow-sm"
    >
      <MatchStrip {...strip} />

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 p-3 @md:gap-3 @md:p-4">
        {side(0)}
        <div className="flex flex-col items-center gap-1.5">
          <p className="sr-only">{t('gamesWonLabel', { a: gamesWon[0], b: gamesWon[1] })}</p>
          <div
            data-score
            aria-hidden="true"
            className="flex items-center gap-2.5 text-[32px] leading-none font-bold @md:text-[42px]"
          >
            {total(0)}
            <span aria-hidden="true" className="text-muted">
              –
            </span>
            {total(1)}
          </div>
          {currentGame && (
            <div
              data-current-game
              className="font-heading flex items-baseline gap-1.5 text-lg leading-none font-bold whitespace-nowrap @md:text-xl"
            >
              <span className="text-muted font-sans text-xs font-medium">
                {t.rich('currentGame', {
                  number: currentGame.number,
                  n: (chunks) => <Numeric>{chunks}</Numeric>,
                })}
              </span>
              <Numeric className="font-heading">
                {currentGame.score[0]}–{currentGame.score[1]}
              </Numeric>
            </div>
          )}
        </div>
        {side(1)}
      </div>

      {(footer || action) && (
        <div
          data-match-footer
          className="border-border text-muted flex flex-wrap items-center gap-x-3 gap-y-2 border-t px-3 py-2.5 text-[13px] @md:px-4"
        >
          <div className="min-w-0 flex-1">{footer}</div>
          {action && (
            <div className="w-full @md:w-auto [&>*]:w-full @md:[&>*]:w-auto">{action}</div>
          )}
        </div>
      )}
    </div>
  );
}
